import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { setRateIdAtom } from "../../../../../../atoms/actions/setRateId/setRateIdAtom";
import { setTransportationTypeIdAtom } from "../../../../../../atoms/actions/setTransportationTypeId/setTransportationTypeIdAtom";
import { CourseLayoutSelectorAtom } from "../../../../../../atoms/selectors/courseLayoutSelectorAtom/courseLayoutSelectorAtom";
import { rateIdSelectorAtom } from "../../../../../../atoms/selectors/rateIdSelectorAtom/rateIdSelectorAtom";
import { TransportationTypeIdSelectorAtom } from "../../../../../../atoms/selectors/transportationTypeIdSelectorAtom/transportationTypeIdSelectorAtom";
import { BundledTransportationTypeSelectNode$key } from "./__generated__/BundledTransportationTypeSelectNode.graphql";

type Props = {
  timeSlotRef: BundledTransportationTypeSelectNode$key | null;
};

export const BundledTransportationTypeSelect = ({ timeSlotRef }: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = useFragment(
    BundledTransportationTypeControlFragmentString,
    timeSlotRef
  );

  const layout = useAtomValue(CourseLayoutSelectorAtom);
  const currentTransportationTypeId = useAtomValue(
    TransportationTypeIdSelectorAtom
  );

  const rateId = useAtomValue(rateIdSelectorAtom);
  const setTransportationTypeId = useSetAtom(setTransportationTypeIdAtom);

  const setRateId = useSetAtom(setRateIdAtom);
  const rates =
    data?.rates?.flatMap((rate) =>
      rate?.rate?.visibility === "ALL" ||
      (rate?.rate?.appliesToMembershipType &&
        rate?.rate?.visibility === "QUALIFYING")
        ? [rate]
        : []
    ) || [];

  const transportationTypes =
    data?.transportationTypes?.flatMap((tt) => (tt ? [tt] : [])) || [];

  const bundledOptions = rates.map((rate) => {
    const ratePriceForHoleCount =
      (layout?.holeCount === "EIGHTEEN"
        ? rate.eighteenHolePrice
        : rate.nineHolePrice) || 0;

    const transportationTypeOptions = transportationTypes.map((tt) => {
      const transportationTypePriceForHoleCount =
        (layout?.holeCount === "EIGHTEEN"
          ? tt.eighteenHolePrice
          : tt.nineHolePrice) || 0;

      const totalCost =
        ratePriceForHoleCount + transportationTypePriceForHoleCount;
      const combinedIds = `${rate.rate.id}.${tt.id}`;

      const transportationIcon = GetTransportIconFromCode(
        tt.customCode || tt?.code || "",
        "mainTiny"
      );

      return {
        value: combinedIds,
        label: (
          <HStack gap={2} alignItems="center">
            {transportationIcon ? transportationIcon({}) : null}
            <Text variant="mainSmallSemiBold">{tt.name}</Text>
            <Text variant="mainSmallSemiBold" color="text-secondary">
              {totalCost
                ? `${currencySymbol}${ConvertCentsToDollars(totalCost)}`
                : ""}
            </Text>
          </HStack>
        ),
        triggerElement: (
          <HStack gap={2} alignItems="center">
            {transportationIcon ? transportationIcon({}) : null}
            <Text variant="mainSmallSemiBold">{`${rate.rate.name} - ${tt.name}`}</Text>
            <Text variant="mainSmallSemiBold" color="text-secondary">
              {totalCost
                ? `${currencySymbol}${ConvertCentsToDollars(totalCost)}`
                : ""}
            </Text>
          </HStack>
        ),
      };
    });

    return {
      label: rate.rate.name,
      options: transportationTypeOptions,
    };
  });
  const currentCombinedId = `${rateId}.${currentTransportationTypeId}`;
  return (
    <DropdownSelect
      testID="bundled-transportation-select"
      hideOuterMargin
      options={bundledOptions}
      value={currentCombinedId}
      onChange={(value) => {
        if (value) {
          const [rateId, ttId] = value.split(".");
          setTransportationTypeId(ttId);
          setRateId(rateId);
        }
      }}
    />
  );
};

const BundledTransportationTypeControlFragmentString = graphql`
  fragment BundledTransportationTypeSelectNode on Slot
  @argumentDefinitions(
    memberId: { type: "ID", defaultValue: null }
    membershipTypeId: { type: "ID", defaultValue: null }
  ) {
    transportationTypes(memberId: $memberId, memberVisible: true) {
      id
      code
      customCode
      name
      enabled
      nineHolePrice: totalPrice(holeCount: NINE)
      eighteenHolePrice: totalPrice(holeCount: EIGHTEEN)
    }
    rates: playerRates(
      type: CUSTOMER
      visibleToMembershipTypeId: $membershipTypeId
    ) {
      rate {
        id
        name
        visibility
        appliesToMembershipType(membershipTypeId: $membershipTypeId)
      }
      nineHolePrice: totalGolfPrice(holeCount: NINE)
      eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
    }
  }
`;
